import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, toQuery, type FieldErrors, type Paged } from './api';
import { rememberPageSize, storedPageSize } from '../components/DataTable';
import { useToast } from '../app/providers';

/**
 * A paged list bound to an endpoint.
 *
 * Every list screen in the app uses this, which is what makes paging and the page-size
 * selector consistent everywhere rather than something each screen reimplements.
 */
export function usePagedList<T>(
  path: string,
  extraParams: Record<string, unknown> = {},
  /**
   * Filters the list opens on, so a screen with a default (Students opens on Active) applies
   * it on the very first request instead of loading everything and then narrowing.
   *
   * Read once, when the hook mounts; later changes go through setFilters.
   */
  initialFilters: Record<string, string> = {},
) {
  const [data, setData] = useState<Paged<T> | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(storedPageSize);
  const [filters, setFiltersState] = useState<Record<string, string>>(initialFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Serialised so the effect below compares by value, not by object identity - otherwise a
  // caller passing an inline object would re-fetch on every render.
  const extra = JSON.stringify(extraParams);

  // Serialised for the same reason as extraParams: the effect below compares by value, and
  // an object literal would be a new reference on every render.
  const filterKey = JSON.stringify(filters);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Blank filters are dropped rather than sent empty, so the query string says exactly
      // what is being asked for and the server's "absent means no restriction" holds.
      const active: Record<string, string> = {};
      for (const [key, value] of Object.entries(JSON.parse(filterKey) as Record<string, string>)) {
        if (value !== '') active[`filter[${key}]`] = value;
      }

      const query = toQuery({ page, pageSize, ...active, ...JSON.parse(extra) });
      setData(await api.get<Paged<T>>(`${path}${query}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the list.');
    } finally {
      setLoading(false);
    }
  }, [path, page, pageSize, filterKey, extra]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    loading,
    error,
    page,
    pageSize,
    filters,
    reload: load,
    setPage,
    setFilters: (value: Record<string, string>) => {
      // A narrower result set makes the current page number meaningless - page 7 of a
      // 3-page result is an empty screen.
      setFiltersState(value);
      setPage(1);
    },
    setPageSize: (size: number) => {
      rememberPageSize(size);
      setPageSize(size);
      setPage(1);
    },
  };
}

/** A single resource fetched once, with a manual reload. */
export function useResource<T>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      setData(await api.get<T>(path));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load.');
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load, setData };
}

/**
 * Runs a mutation, toasts the outcome and reports busy state.
 *
 * Centralising this is what guarantees the "toast on every save, update and delete"
 * requirement actually holds - a screen cannot forget, because the toast is not its job.
 */
export function useMutation() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T,>(
      action: () => Promise<T>,
      successMessage: string,
      onSuccess?: (result: T) => void | Promise<void>,
      /**
       * Receives the per-field messages from a rejected save, so a form can mark the inputs
       * that failed. Empty for errors that belong to no particular field.
       */
      onFieldErrors?: (errors: FieldErrors) => void,
    ): Promise<boolean> => {
      setBusy(true);
      try {
        const result = await action();
        toast.success(successMessage);
        await onSuccess?.(result);
        return true;
      } catch (err) {
        // The server's own wording, carried over verbatim from the legacy validation.
        toast.error(err instanceof Error ? err.message : 'Something went wrong.');
        onFieldErrors?.(err instanceof ApiError ? err.fieldErrors : {});
        return false;
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );

  return { run, busy };
}

export const money = (value: number | null | undefined) =>
  (value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const monthYear = (month?: number | null, year?: number | null) =>
  month && year ? `${month}/${year}` : '—';
